<script lang="ts">
  import { Eye, EyeOff, AlertCircle, ShieldCheck, Copy, Check } from 'lucide-svelte';
  import {
    login as apiLogin,
    loginVerify,
    twoFASetup,
    twoFAEnable,
    type TwoFASetup
  } from '../auth';
  import { setError, clearError, setLoading, uiState } from '$lib/state/ui.svelte';

  type Step = 'password' | 'code' | 'setup' | 'recovery';

  let step = $state<Step>('password');
  let loginForm = $state({ username: '', password: '' });
  let showPassword = $state(false);
  let isAuthenticated = $state(false);

  let mfaToken = $state('');
  let code = $state('');
  let setup = $state<TwoFASetup | null>(null);
  let recoveryCodes = $state<string[]>([]);
  let recoverySaved = $state(false);
  let copied = $state(false);

  function complete() {
    isAuthenticated = true;
    window.dispatchEvent(new CustomEvent('userAuthenticated'));
  }

  async function submitPassword() {
    if (!loginForm.username.trim() || !loginForm.password.trim()) {
      setError('Username and password are required');
      return;
    }
    setLoading(true);
    clearError();
    try {
      const challenge = await apiLogin(loginForm.username, loginForm.password);
      mfaToken = challenge.mfa_token;
      code = '';
      if (challenge.status === 'mfa_required') {
        step = 'code';
      } else {
        setup = await twoFASetup(mfaToken);
        step = 'setup';
      }
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function submitCode() {
    if (code.trim().length < 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    clearError();
    try {
      await loginVerify(mfaToken, code.trim());
      complete();
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Invalid code.');
    } finally {
      setLoading(false);
    }
  }

  async function submitEnable() {
    if (code.trim().length < 6) {
      setError('Enter the 6-digit code shown in your authenticator app');
      return;
    }
    setLoading(true);
    clearError();
    try {
      const data = await twoFAEnable(mfaToken, code.trim());
      recoveryCodes = data.recovery_codes ?? [];
      step = 'recovery';
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'Invalid code.');
    } finally {
      setLoading(false);
    }
  }

  async function copyRecovery() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch {
      /* clipboard may be unavailable; codes are visible on screen */
    }
  }
</script>

{#if !isAuthenticated}
  <div class="min-h-screen bg-toyama-navy flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="flex justify-center mb-4">
        <img src="/images/toyama-logo.svg" alt="Toyama" class="h-12" />
      </div>
      <h2 class="mt-4 text-center text-2xl font-display font-bold text-white">RCU Configuration</h2>
      <p class="mt-2 text-center text-sm text-toyama-cloudy-gray">
        {#if step === 'password'}
          Sign in to access the configuration tool
        {:else if step === 'code'}
          Enter your two-factor code
        {:else if step === 'setup'}
          Set up two-factor authentication
        {:else}
          Save your recovery codes
        {/if}
      </p>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-bg-canvas py-8 px-4 shadow sm:rounded-lg sm:px-10">
        {#if step === 'password'}
          <form
            class="space-y-6"
            onsubmit={(e) => {
              e.preventDefault();
              submitPassword();
            }}
          >
            <div>
              <label for="username" class="block text-sm font-medium text-text-secondary">
                Username
              </label>
              <div class="mt-1">
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  autocomplete="username"
                  bind:value={loginForm.username}
                  class="appearance-none block w-full px-3 py-2 border border-border-strong rounded-md placeholder-gray-400 focus:outline-none focus:ring-toyama-teal focus:border-toyama-teal sm:text-sm"
                  placeholder="Enter username"
                />
              </div>
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-text-secondary">
                Password
              </label>
              <div class="mt-1 relative">
                {#if showPassword}
                  <input
                    id="password"
                    name="password"
                    type="text"
                    required
                    autocomplete="current-password"
                    bind:value={loginForm.password}
                    class="appearance-none block w-full px-3 py-2 pr-10 border border-border-strong rounded-md placeholder-gray-400 focus:outline-none focus:ring-toyama-teal focus:border-toyama-teal sm:text-sm"
                    placeholder="Enter password"
                  />
                {:else}
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autocomplete="current-password"
                    bind:value={loginForm.password}
                    class="appearance-none block w-full px-3 py-2 pr-10 border border-border-strong rounded-md placeholder-gray-400 focus:outline-none focus:ring-toyama-teal focus:border-toyama-teal sm:text-sm"
                    placeholder="Enter password"
                  />
                {/if}
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onclick={() => (showPassword = !showPassword)}
                >
                  {#if showPassword}
                    <EyeOff class="h-5 w-5 text-text-muted" />
                  {:else}
                    <Eye class="h-5 w-5 text-text-muted" />
                  {/if}
                </button>
              </div>
            </div>

            {#if uiState.errorMessage}
              <div class="rounded-md bg-red-50 p-4">
                <div class="flex">
                  <AlertCircle class="h-5 w-5 text-red-400" />
                  <div class="ml-3">
                    <p class="text-sm text-red-700">{uiState.errorMessage}</p>
                  </div>
                </div>
              </div>
            {/if}

            <button
              type="submit"
              disabled={uiState.isLoading}
              class="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-toyama-navy bg-toyama-lime hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-toyama-lime disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {#if uiState.isLoading}
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-toyama-navy mr-2"></div>
                Signing in...
              {:else}
                Sign in
              {/if}
            </button>
          </form>
        {:else if step === 'code'}
          <form
            class="space-y-6"
            onsubmit={(e) => {
              e.preventDefault();
              submitCode();
            }}
          >
            <div class="flex justify-center text-toyama-teal">
              <ShieldCheck class="h-10 w-10" />
            </div>
            <p class="text-center text-sm text-text-secondary">
              Open your authenticator app and enter the 6-digit code, or use a recovery code.
            </p>
            <input
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              bind:value={code}
              placeholder="123456"
              class="appearance-none block w-full px-3 py-3 text-center text-2xl tracking-[0.5em] font-mono border border-border-strong rounded-md focus:outline-none focus:ring-toyama-teal focus:border-toyama-teal"
            />
            {#if uiState.errorMessage}
              <p class="text-sm text-red-700 text-center">{uiState.errorMessage}</p>
            {/if}
            <button
              type="submit"
              disabled={uiState.isLoading}
              class="w-full flex justify-center py-2.5 px-4 rounded-md text-sm font-medium text-toyama-navy bg-toyama-lime hover:opacity-90 disabled:opacity-50 transition-all"
            >
              {uiState.isLoading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        {:else if step === 'setup' && setup}
          <form
            class="space-y-5"
            onsubmit={(e) => {
              e.preventDefault();
              submitEnable();
            }}
          >
            <p class="text-sm text-text-secondary">
              Scan this QR code with Google Authenticator, Authy, or 1Password, then enter the
              6-digit code it shows.
            </p>
            <div class="flex justify-center">
              <img src={setup.qr} alt="Authenticator QR code" class="rounded-md border border-border-strong" />
            </div>
            <p class="text-xs text-text-muted text-center break-all">
              Can't scan? Enter this key manually:<br />
              <span class="font-mono text-text-secondary">{setup.secret}</span>
            </p>
            <input
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              bind:value={code}
              placeholder="123456"
              class="appearance-none block w-full px-3 py-3 text-center text-2xl tracking-[0.5em] font-mono border border-border-strong rounded-md focus:outline-none focus:ring-toyama-teal focus:border-toyama-teal"
            />
            {#if uiState.errorMessage}
              <p class="text-sm text-red-700 text-center">{uiState.errorMessage}</p>
            {/if}
            <button
              type="submit"
              disabled={uiState.isLoading}
              class="w-full flex justify-center py-2.5 px-4 rounded-md text-sm font-medium text-toyama-navy bg-toyama-lime hover:opacity-90 disabled:opacity-50 transition-all"
            >
              {uiState.isLoading ? 'Enabling...' : 'Enable two-factor'}
            </button>
          </form>
        {:else if step === 'recovery'}
          <div class="space-y-5">
            <div class="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              Save these recovery codes somewhere safe. Each can be used once if you lose your
              authenticator. <strong>They won't be shown again.</strong>
            </div>
            <div class="grid grid-cols-2 gap-2 font-mono text-sm bg-bg-subtle rounded-md p-4">
              {#each recoveryCodes as rc (rc)}
                <span class="text-text-secondary">{rc}</span>
              {/each}
            </div>
            <button
              type="button"
              onclick={copyRecovery}
              class="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium border border-border-strong text-text-secondary hover:bg-bg-subtle"
            >
              {#if copied}
                <Check class="h-4 w-4" /> Copied
              {:else}
                <Copy class="h-4 w-4" /> Copy codes
              {/if}
            </button>
            <label class="flex items-center gap-2 text-sm text-text-secondary">
              <input type="checkbox" bind:checked={recoverySaved} class="rounded border-border-strong" />
              I've saved my recovery codes
            </label>
            <button
              type="button"
              disabled={!recoverySaved}
              onclick={complete}
              class="w-full flex justify-center py-2.5 px-4 rounded-md text-sm font-medium text-toyama-navy bg-toyama-lime hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Continue to dashboard
            </button>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}
